import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "modeRadio",
    "customPanel",
    "decreaseBtn",
    "increaseBtn",
    "valueDisplay",
    "valueInput",
    "diffBadge",
  ];

  static values = {
    recommended: Number,
  };

  // Keep these mirrored with CalorieOverrideOptions on the server.
  static MAX_STEPS = 5;
  static STEP_PERCENT = 0.1;

  connect() {
    this.step = Math.max(1, Math.round(this.recommendedValue * this.constructor.STEP_PERCENT));
    // Derive bounds from step_size × MAX_STEPS (not raw percentages) so the
    // ladder rungs match the server's CalorieOverrideOptions exactly.
    // recommended * 1.5 can round up past the last legal rung when step_size
    // itself was rounded (e.g. recommended=471 → step=47 → last rung=706, not 707).
    this.min = this.recommendedValue - this.constructor.MAX_STEPS * this.step;
    this.max = this.recommendedValue + this.constructor.MAX_STEPS * this.step;
    this.#syncMode();
    this.#render();
  }

  toggleMode() {
    this.#syncMode();
    if (this.#useCustom()) {
      // If we toggled back to custom from a cleared state, seed from the recommendation
      // so the stepper has a starting point on the ladder.
      if (this.valueInputTarget.value === "") {
        this.#setValue(this.recommendedValue);
      } else if (!this.#inRange(this.#currentValue())) {
        this.#setValue(this.recommendedValue);
      }
    } else {
      // Recommended mode: clear the hidden value so the form posts an empty string,
      // which Rails coerces to nil and clears the override.
      this.valueInputTarget.value = "";
    }
  }

  // Re-syncs the visible UI from the (already-reset-by-section-edit) input state.
  // Chained on Discard Changes. section_edit#cancel defers its #resetFields
  // call behind a 150ms animation timer, so we wait slightly longer before
  // reading the freshly-reset DOM values.
  reset() {
    setTimeout(() => {
      this.#syncMode();
      this.#render();
    }, 200);
  }

  increase(event) {
    event.preventDefault();
    this.#stepBy(1);
  }

  decrease(event) {
    event.preventDefault();
    this.#stepBy(-1);
  }

  // Steps along the ladder rungs (recommended + i*step). Snaps to the nearest
  // rung first so an off-ladder starting value (e.g. a server-rejected save
  // that left a stale custom_daily_calories in the form) can't propagate.
  #stepBy(delta) {
    const currentIdx = Math.round((this.#currentValue() - this.recommendedValue) / this.step);
    const maxSteps = this.constructor.MAX_STEPS;
    const nextIdx = Math.min(maxSteps, Math.max(-maxSteps, currentIdx + delta));
    this.#setValue(this.recommendedValue + nextIdx * this.step);
  }

  #syncMode() {
    this.customPanelTarget.hidden = !this.#useCustom();
  }

  #useCustom() {
    const checked = this.modeRadioTargets.find((r) => r.checked);
    return checked?.value === "custom";
  }

  #currentValue() {
    const v = parseInt(this.valueInputTarget.value, 10);
    return Number.isFinite(v) ? v : this.recommendedValue;
  }

  #inRange(v) {
    return v >= this.min && v <= this.max;
  }

  #setValue(v) {
    const clamped = Math.min(this.max, Math.max(this.min, v));
    this.valueInputTarget.value = clamped;
    this.#render();
  }

  #render() {
    const v = this.#currentValue();
    this.valueDisplayTarget.textContent = v;
    this.decreaseBtnTarget.disabled = v <= this.min;
    this.increaseBtnTarget.disabled = v >= this.max;
    const diff = Math.round(((v - this.recommendedValue) / this.recommendedValue) * 100);
    const sign = diff > 0 ? "+" : "";
    this.diffBadgeTarget.textContent = `${sign}${diff}%`;
  }
}
